import { useQuery } from '@tanstack/react-query';
import { fetchOnboardingStateFromServer, type OnboardingState } from '../utils/net/onboardingApi';

export function useOnboardingState() {
  const { data, isLoading, error } = useQuery<OnboardingState>({
    queryKey: ['onboarding-state'],
    queryFn: fetchOnboardingStateFromServer,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    // Дані завантажуються блокуюче в ServerConnectGate, тому тут не блокуємо UI
    enabled: true,
  });

  // Helper functions that work directly with server data
  // Single source of truth: onboarding_completed = fully done (tap + map tutorial + reward claimed).
  const hasOnboardingCompleted = () => {
    if (!data) return false;
    return !!data.onboarding_completed;
  };

  // A fully-completed onboarding (onboarding_completed) implies every sub-step is
  // done. The per-step month flags (tap/map/welcome) are legacy and can be unset
  // even for completed users (e.g. onboarding force-completed server-side), which
  // would otherwise wrongly re-trigger the tap tutorial / map welcome / map guide.
  // Treat onboarding_completed as the authoritative override for all of them.
  const hasTapOnboardingDone = () => {
    if (!data) return false;
    return !!data.onboarding_completed || !!data.tap_onboarding_month;
  };

  const hasMapOnboardingDone = () => {
    if (!data) return false;
    return !!data.onboarding_completed || !!data.map_onboarding_month;
  };

  const hasMapWelcomeOnboardingDone = () => {
    if (!data) return false;
    return !!data.onboarding_completed || !!data.map_welcome_month;
  };

  const hasLanguageSelectionBeenShown = () => {
    return data?.language_selection_shown ?? false;
  };

  const hasSoundSelectionBeenShown = () => {
    return data?.sound_selection_shown ?? false;
  };

  return {
    data,
    isLoading,
    error,
    hasOnboardingCompleted,
    hasTapOnboardingDone,
    hasMapOnboardingDone,
    hasMapWelcomeOnboardingDone,
    hasLanguageSelectionBeenShown,
    hasSoundSelectionBeenShown,
  };
}

